import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Pencil, Trash2 } from 'lucide-react';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import type { Team, Manager } from '@/types';
import { InternalTeamForm } from './InternalTeamForm';
import { DeleteTeamDialog } from '../teams/DeleteTeamDialog';

interface InternalTeamsTabProps {
  teams: Team[];
  managers: Manager[];
  onAddTeam: (teamData: Omit<Team, 'id'>) => Promise<void>;
  onUpdateTeam: (teamId: string, updates: Partial<Team>) => Promise<void>;
  onDeleteTeam: (teamId: string) => Promise<void>;
  isLoading: boolean;
}

const InternalTeamsTab: React.FC<InternalTeamsTabProps> = ({
  teams,
  managers,
  onAddTeam,
  onUpdateTeam,
  onDeleteTeam,
  isLoading
}) => {
  const [isAddingTeam, setIsAddingTeam] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | undefined>(undefined);
  const [deletingTeam, setDeletingTeam] = useState<Team | null>(null);
  const [teamTypeFilter, setTeamTypeFilter] = useState<'ALL' | 'INTERNAL_PM' | 'BIZ_DEV'>('ALL');
  const [searchTerm, setSearchTerm] = useState('');

  // Filter teams to only show INTERNAL_PM and BIZ_DEV types
  const internalTeams = useMemo(
    () => teams.filter((team) => team.type === 'INTERNAL_PM' || team.type === 'BIZ_DEV'),
    [teams]
  );

  const handleAddTeam = async (teamData: Omit<Team, 'id'>) => {
    try {
      await onAddTeam(teamData);
      setIsAddingTeam(false);
    } catch (error) {
      console.error('Error adding internal team:', error);
      throw error;
    }
  };

  const handleUpdateTeam = async (teamData: Omit<Team, 'id'>) => {
    if (!editingTeam) return;
    try {
      await onUpdateTeam(editingTeam.id, teamData);
      setEditingTeam(undefined);
    } catch (error) {
      console.error('Error updating internal team:', error);
      throw error;
    }
  };

  const handleDeleteTeam = async () => {
    if (!deletingTeam) return;
    try {
      await onDeleteTeam(deletingTeam.id);
      setDeletingTeam(null);
    } catch (error) {
      console.error('Error deleting internal team:', error);
    }
  };

  const filteredTeams = internalTeams.filter(team => {
    const matchesType = teamTypeFilter === 'ALL' || team.type === teamTypeFilter;
    const matchesSearch = team.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesType && matchesSearch;
  });

  return (
    <div className="space-y-6">
      {/* Add/Edit Team Form */}
      <InternalTeamForm
        open={isAddingTeam || !!editingTeam}
        onOpenChange={(open) => {
          if (!open) {
            setIsAddingTeam(false);
            setEditingTeam(undefined);
          }
        }}
        initialData={editingTeam}
        onSubmit={editingTeam ? handleUpdateTeam : handleAddTeam}
        onCancel={() => {
          setIsAddingTeam(false);
          setEditingTeam(undefined);
        }}
        isSubmitting={isLoading}
        managers={managers}
      />

      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <SearchableSelect
            value={teamTypeFilter}
            onValueChange={(value: string) => setTeamTypeFilter(value as 'ALL' | 'INTERNAL_PM' | 'BIZ_DEV')}
            options={[
              { label: 'All Teams', value: 'ALL' },
              { label: 'Internal PM', value: 'INTERNAL_PM' },
              { label: 'Business Development', value: 'BIZ_DEV' }
            ]}
            placeholder="Filter by team type"
            className="w-[180px] h-10"
          />
          <Input
            placeholder="Search teams..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-48"
          />
        </div>
        <Button
          onClick={() => setIsAddingTeam(true)}
          className="hidden"
          data-add-internal-team-button
        >
          Add Internal Team
        </Button>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Manager</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                  Loading internal teams...
                </TableCell>
              </TableRow>
            ) : filteredTeams.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                  No internal teams found
                </TableCell>
              </TableRow>
            ) : (
              filteredTeams.map((team) => (
                <TableRow key={team.id}>
                  <TableCell className="font-medium">{team.name}</TableCell>
                  <TableCell>
                    {team.type === 'INTERNAL_PM' ? 'Internal PM' : 'Business Development'}
                  </TableCell>
                  <TableCell>{team.manager_name || 'N/A'}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end space-x-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setEditingTeam(team)}
                        className="h-8 w-8"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeletingTeam(team)}
                        className="h-8 w-8 text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Delete Confirmation Dialog */}
      <DeleteTeamDialog
        open={!!deletingTeam}
        onOpenChange={(open) => !open && setDeletingTeam(null)}
        onConfirm={handleDeleteTeam}
        teamName={deletingTeam?.name || ''}
      />
    </div>
  );
};

export default InternalTeamsTab;
